import { forwardRef, useEffect, useId, useState } from "react";
import { Popover } from "../../../Popover";
import { Listbox } from "../../../Listbox";
import ArrowDropDown from "@servicetitan/hammer-icon/mdi/round/arrow_drop_down.svg";
import { ItemType } from "../../../Listbox/Listbox";
import { ToolbarItemAppearance } from "../../sharedTypes";
import { ToolbarItemWrapper } from "../ToolbarItemWrapper/ToolbarItemWrapper";
import { useOverFlowItemContext } from "../ToolbarOverflowMenu/ToolbarOverflowMenu";

import cx from "classnames";
import styles from "../../Toolbar.module.scss";

/**
 * Type for items in the ToolbarSelect dropdown
 * @extends ItemType<T & { id: string }>
 */
type ToolbarSelectItem<T = Record<string, unknown>> = ItemType<
  T & {
    id: string;
  }
>;

/**
 * Props for the ToolbarSelect component
 * @extends Omit<React.ComponentPropsWithoutRef<typeof Popover.Button>, "appearance" | "onChange">
 */
type ToolbarSelectProps<T = Record<string, unknown>> = Omit<
  React.ComponentPropsWithoutRef<typeof Popover.Button>,
  "appearance" | "onChange"
> & {
  /**
   * Visual appearance of the select button
   * @default ghost
   */
  appearance?: ToolbarItemAppearance;
  /**
   * Callback function when selection changes
   */
  onChange?: (optionId: string) => void;
  /**
   * Currently selected item ID (controlled mode)
   */
  selected?: string;
  /**
   * Array of selectable items
   */
  items: ToolbarSelectItem<T>[];
  /**
   * Accessible label for the select component
   */
  accessibleLabel: string;
};

/**
 * ToolbarSelect component provides a dropdown selector within a toolbar.
 *
 * Features:
 * - Dropdown menu with selectable options
 * - Controlled and uncontrolled usage patterns
 * - Automatic overflow menu integration
 * - Accessibility support with proper ARIA labels
 * - Small size optimized for toolbar layout
 * - Ghost and primary appearance options
 * - Dropdown arrow icon indicator
 * - Listbox integration for keyboard navigation
 *
 * @example
 * <ToolbarSelect
 *   items={fontOptions}
 *   selected="arial"
 *   accessibleLabel="Font family"
 *   onChange={(id) => setFont(id)}
 * />
 *
 * @example
 * <ToolbarSelect
 *   appearance="primary"
 *   items={colorOptions}
 *   accessibleLabel="Text color"
 * />
 */
const ToolbarSelect = forwardRef<HTMLButtonElement, ToolbarSelectProps>(
  (props, _ref) => {
    const {
      appearance = "ghost",
      onChange,
      selected: selectedItemId,
      accessibleLabel,
      className,
      items,
      ...rest
    } = props;
    const selectId = useId();

    // Internal state for uncontrolled mode
    const defaultId = items.length > 0 ? items[0].id : "";
    const [internalSelectedId, setInternalSelectedId] = useState(defaultId);

    // Use controlled value if provided, otherwise use internal state
    const selectedId = selectedItemId ?? internalSelectedId;

    // Find the selected item by ID (or default to first item using defaultId)
    const selectedItem =
      items.find((item) => item.id === selectedId) ||
      items.find((item) => item.id === defaultId);

    // Update internal selection when items change and current selection is no longer valid
    useEffect(() => {
      if (
        !selectedItemId &&
        items.length > 0 &&
        !items.some((item) => item.id === internalSelectedId)
      ) {
        setInternalSelectedId(items[0].id);
      }
    }, [items, selectedItemId, internalSelectedId]);

    // Handle selection change
    const handleSelectionChange = (
      selected: ToolbarSelectItem<Record<string, unknown>> | undefined,
    ) => {
      if (selected?.id) {
        if (selectedItemId === undefined) {
          setInternalSelectedId(selected.id);
        }

        onChange?.(selected.id);
      }
    };

    // Checks if this component is rendered inside the overflow menu and fallbacks when the context is unavailable
    const { isInOverflowMenu = false } = useOverFlowItemContext?.() || {};

    const buttonClassNames = cx(className, styles["toolbar-button-item"]);

    const item = (
      <Popover noPadding>
        <Popover.Button
          appearance={appearance}
          size="small"
          icon={{ after: ArrowDropDown }}
          className={buttonClassNames}
          id={selectId}
          data-anv="toolbar-select"
          {...rest}
        >
          {selectedItem?.label}
        </Popover.Button>
        <Popover.Content
          className={styles["toolbar-select-content"]}
          aria-label={`${accessibleLabel} options`}
        >
          <Listbox
            selected={selectedItem}
            onSelectionChange={handleSelectionChange}
            items={items}
            aria-labelledby={selectId}
          >
            {({ items }) =>
              items.map((item) => (
                <Listbox.Option key={item.label} item={item}>
                  {item.label}
                </Listbox.Option>
              ))
            }
          </Listbox>
        </Popover.Content>
      </Popover>
    );

    if (isInOverflowMenu) {
      return item;
    }

    return (
      <ToolbarItemWrapper item={{ itemProps: props, itemType: "select" }}>
        {item}
      </ToolbarItemWrapper>
    );
  },
);

ToolbarSelect.displayName = "ToolbarSelect";

export { type ToolbarSelectProps, ToolbarSelect };
